/** Componentes só visuais. Lógica de negócio vem de @lindeza/core. */
import { bookingLink } from '@lindeza/core';
import { useProfessional, useRuntime } from '@lindeza/core/hooks';
import { type ReactNode, useEffect, useId, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { toast, useUi } from '../app/ui';
import { IconCalendar, IconChevronLeft, IconCopy, IconEllipsis, IconMegaphone, IconPackage, IconPlus, IconSettings, IconUsers, IconWallet, IconX } from './Icons';
import { SyncBadge } from './SyncBadge';

/** Abre "Marcar atendimento" de qualquer tela: a Agenda lê ?marcar=1. */
function useOpenNew() {
  const navigate = useNavigate();
  const location = useLocation();
  return () => {
    const onAgenda = location.pathname === '/' || location.pathname.startsWith('/agenda/');
    navigate(`${onAgenda ? location.pathname : '/'}?marcar=1`);
  };
}

/** Celular e tablet: doca embaixo com Agenda, Clientes, Marcar, Caixa e Mais (Estoque, Divulgar e Ajustes ficam em Mais). */
export function Dock() {
  const openNew = useOpenNew();
  const location = useLocation();
  const inMore =
    location.pathname.startsWith('/ajustes') || location.pathname.startsWith('/divulgar') || location.pathname.startsWith('/estoque') || location.pathname === '/mais';
  const item = (to: string, label: string, Icon: typeof IconCalendar, end?: boolean, forceActive?: boolean) => (
    <NavLink to={to} end={end} className={({ isActive }) => (isActive || forceActive ? 'active' : undefined)}>
      <span className="pill">
        <Icon />
      </span>
      {label}
    </NavLink>
  );
  return (
    <nav className="dock" aria-label="Principal">
      {item('/', 'Agenda', IconCalendar, false, location.pathname === '/' || location.pathname.startsWith('/agenda'))}
      {item('/clientes', 'Clientes', IconUsers)}
      <button type="button" className="dock-plus" onClick={openNew} aria-label="Marcar atendimento">
        <IconPlus strokeWidth={2.75} />
        Marcar
      </button>
      {item('/caixa', 'Caixa', IconWallet)}
      {item('/mais', 'Mais', IconEllipsis, false, inMore)}
    </nav>
  );
}

/** Computador: trilho à esquerda com as 6 áreas, o link para marcar e o selo de salvo. */
export function Rail() {
  const rt = useRuntime();
  const prof = useProfessional();
  const openNew = useOpenNew();
  const location = useLocation();
  const link = bookingLink(rt.publicBaseUrl, prof?.slug);
  const tabs = [
    { to: '/', label: 'Agenda', icon: IconCalendar, active: location.pathname === '/' || location.pathname.startsWith('/agenda') },
    { to: '/clientes', label: 'Clientes', icon: IconUsers },
    { to: '/caixa', label: 'Caixa', icon: IconWallet },
    { to: '/estoque', label: 'Estoque', icon: IconPackage },
    { to: '/divulgar', label: 'Divulgar', icon: IconMegaphone },
    { to: '/ajustes', label: 'Ajustes', icon: IconSettings, active: location.pathname.startsWith('/ajustes') || location.pathname === '/mais' },
  ];
  return (
    <aside className="rail" aria-label="Áreas">
      <div className="rail-brand">
        <span className="rail-logo">Lindeza</span>
        <span className="rail-studio ellipsis">{prof?.business_name ?? prof?.name}</span>
      </div>
      <button type="button" className="rail-new" onClick={openNew}>
        <span className="ic">
          <IconPlus />
        </span>
        Marcar atendimento
      </button>
      <nav className="rail-nav" aria-label="Principal">
        {tabs.map(({ to, label, icon: Icon, active }) => (
          <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => (isActive || active ? 'active' : undefined)}>
            <Icon />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="rail-foot">
        {link && (
          <div className="rail-link">
            <span>Seu link para marcar</span>
            <b>{link.replace(/^https?:\/\//, '')}</b>
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() =>
                navigator.clipboard.writeText(link).then(
                  () => toast('Link copiado.'),
                  () => toast('Não deu para copiar. Segure o texto para copiar.', { tone: 'error' }),
                )
              }
            >
              <IconCopy /> Copiar link
            </button>
          </div>
        )}
        <SyncBadge />
      </div>
    </aside>
  );
}

export function TopBar({
  title,
  sub,
  back,
  backLabel = 'Voltar',
  right,
}: {
  title: ReactNode;
  sub?: ReactNode;
  back?: () => void;
  /** texto do "‹ Voltar" (ex.: "Mais") */
  backLabel?: string;
  right?: ReactNode;
}) {
  return (
    <header className={`topbar${back ? ' topbar--back' : ''}`}>
      {back && (
        <button className="back-link" onClick={back} aria-label={backLabel === 'Voltar' ? 'Voltar' : `Voltar para ${backLabel}`}>
          <IconChevronLeft /> {backLabel}
        </button>
      )}
      <h1>
        {title}
        {sub && <span className="sub">{sub}</span>}
      </h1>
      {right}
    </header>
  );
}

export function Sheet({
  title,
  sub,
  onClose,
  children,
  labelledBy,
}: {
  title: ReactNode;
  sub?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  labelledBy?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const autoId = useId();
  const titleId = labelledBy ?? `sheet-${autoId}`;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <div
      className="sheet-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} ref={ref}>
        <div className="sheet__grip" />
        <div className="sheet__head">
          <h2 id={titleId}>
            {title}
            {sub && <span className="sub">{sub}</span>}
          </h2>
          <button className="icon-btn icon-btn--filled" onClick={onClose} aria-label="Fechar">
            <IconX />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Toasts() {
  const toasts = useUi((s) => s.toasts);
  const dismiss = useUi((s) => s.dismiss);
  return (
    <div className="toasts" aria-live="polite" role="status">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.tone === 'error' ? 'toast--error' : ''}`}>
          <p>{t.text}</p>
          {t.action && (
            <button
              className="btn"
              onClick={() => {
                t.action!.run();
                dismiss(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && (
        <span className="error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function Switch({
  label,
  hint,
  checked,
  onChange,
}: {
  label: ReactNode;
  hint?: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const id = useId();
  return (
    <label className="switch" htmlFor={id}>
      <span className="grow">
        <span style={{ fontWeight: 600 }}>{label}</span>
        {hint && <span className="small muted" style={{ display: 'block' }}>{hint}</span>}
      </span>
      <input id={id} type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Array<{ value: T; label: ReactNode }>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Empty({ icon, children, action }: { icon?: ReactNode; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon}
      <p>{children}</p>
      {action}
    </div>
  );
}

export function Avatar({ name, size }: { name: string; size?: 'lg' }) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  return <span className={`avatar${size ? ` avatar--${size}` : ''}`}>{initials || '?'}</span>;
}

/** Seletor de hora em passos (ex.: 15 em 15 min). */
export function TimeSelect({
  value,
  onChange,
  step = 15,
  from = 0,
  to = 24 * 60,
  id,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  step?: number;
  from?: number;
  to?: number;
  id?: string;
  ariaLabel?: string;
}) {
  const options: string[] = [];
  for (let m = from; m <= to; m += step) {
    const h = Math.floor(m / 60);
    options.push(`${String(h).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  }
  if (!options.includes(value)) options.push(value);
  options.sort();
  return (
    <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value)} aria-label={ariaLabel}>
      {options.map((o) => (
        <option key={o} value={o}>
          {o.replace(':', 'h')}
        </option>
      ))}
    </select>
  );
}
